import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { subscribePush, unsubscribePush, isPushSubscribed, pushSupported } from "@/lib/push";
import { GOAL_LABELS, ACTIVITY_LABELS } from "@/lib/nutrition";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  User, Target, Flame, Salad, Bell, Shield, Settings, LogOut, ChevronRight, Pencil, Loader2,
} from "lucide-react";

const AVATAR = "https://images.unsplash.com/photo-1740252117044-2af197eea287?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHx1c2VyJTIwYXZhdGFyJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzg3ODM2NTg1fDA&ixlib=rb-4.1.0&q=85&w=200";
const DIETS = ["No preference", "Vegetarian", "Vegan", "High protein", "Low carb", "Mediterranean", "Other"];

export default function Profile() {
  const { profile, setProfile, signOut, session } = useAuth();
  const [editOpen, setEditOpen] = useState(false);
  const [goalOpen, setGoalOpen] = useState(false);
  const [dietOpen, setDietOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  if (!profile) return null;

  return (
    <div className="px-5 pt-8 space-y-6">
      <div className="flex flex-col items-center text-center gap-3 pt-2">
        <img src={profile.avatar_url || AVATAR} alt="avatar" className="w-24 h-24 rounded-full object-cover border-4 border-card shadow-sm" />
        <div>
          <h1 className="font-display text-2xl font-medium tracking-tight" data-testid="profile-name">{profile.name}</h1>
          <p className="text-muted-foreground text-sm">{session?.user?.email}</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <MiniStat label="Current" value={`${profile.current_weight || "—"}kg`} />
        <MiniStat label="Target" value={`${profile.target_weight || "—"}kg`} />
        <MiniStat label="Daily goal" value={`${profile.calorie_goal || "—"}`} sub="kcal" />
      </div>

      <div className="rounded-3xl bg-card border border-border overflow-hidden">
        <Row icon={Pencil} label="Edit profile" onClick={() => setEditOpen(true)} testid="edit-profile-row" />
        <Row icon={Flame} label="Change calorie goal" value={`${profile.calorie_goal} kcal`} onClick={() => setGoalOpen(true)} testid="goal-row" />
        <Row icon={Salad} label="Dietary preference" value={profile.dietary_preference || "None"} onClick={() => setDietOpen(true)} testid="diet-row" />
        <Row icon={Bell} label="Notifications" onClick={() => setNotifOpen(true)} testid="notif-row" />
        <Row icon={Shield} label="Privacy" onClick={() => toast.info("Your data is private and secured with Row Level Security.")} testid="privacy-row" />
        <Row icon={Settings} label="Account settings" onClick={() => toast.info(`Signed in as ${session?.user?.email}`)} last testid="account-row" />
      </div>

      <div className="rounded-2xl bg-card border border-border p-4 text-sm text-muted-foreground">
        <div className="flex items-center gap-2 mb-1 text-foreground font-semibold"><Target className="w-4 h-4 text-primary" /> Goal</div>
        {GOAL_LABELS[profile.goal] || "—"} · {ACTIVITY_LABELS[profile.activity_level] || "—"}
      </div>

      <Button onClick={signOut} data-testid="logout-btn" variant="outline" className="w-full rounded-full py-6 text-destructive border-destructive/30 hover:bg-destructive/5">
        <LogOut className="w-4 h-4" /> Log out
      </Button>
      <div className="h-2" />

      <EditProfile open={editOpen} setOpen={setEditOpen} profile={profile} setProfile={setProfile} />
      <GoalDialog open={goalOpen} setOpen={setGoalOpen} profile={profile} setProfile={setProfile} />
      <DietDialog open={dietOpen} setOpen={setDietOpen} profile={profile} setProfile={setProfile} />
      <NotifDialog open={notifOpen} setOpen={setNotifOpen} profile={profile} setProfile={setProfile} />
    </div>
  );
}

function MiniStat({ label, value, sub }) {
  return (
    <div className="rounded-2xl bg-card border border-border p-3 text-center">
      <div className="text-lg font-display font-medium tabular-nums">{value}</div>
      <div className="text-[10px] text-muted-foreground">{label}{sub ? ` (${sub})` : ""}</div>
    </div>
  );
}

function Row({ icon: Icon, label, value, onClick, last, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`w-full flex items-center gap-3 p-4 hover:bg-secondary/40 transition-colors ${last ? "" : "border-b border-border"}`}>
      <div className="w-9 h-9 rounded-xl bg-secondary flex items-center justify-center"><Icon className="w-4.5 h-4.5 text-primary" /></div>
      <span className="flex-1 text-left font-medium text-sm">{label}</span>
      {value && <span className="text-sm text-muted-foreground">{value}</span>}
      <ChevronRight className="w-4 h-4 text-muted-foreground" />
    </button>
  );
}

function useSave(setProfile, setOpen) {
  const [busy, setBusy] = useState(false);
  const save = async (payload) => {
    setBusy(true);
    try {
      const p = await api.updateProfile(payload);
      setProfile(p);
      toast.success("Saved");
      setOpen(false);
    } catch (e) {
      toast.error(e.message || "Could not save");
    } finally {
      setBusy(false);
    }
  };
  return { busy, save };
}

function EditProfile({ open, setOpen, profile, setProfile }) {
  const [f, setF] = useState({});
  const { busy, save } = useSave(setProfile, setOpen);
  const val = (k) => (f[k] !== undefined ? f[k] : profile[k] ?? "");
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-sm rounded-3xl" data-testid="edit-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">Edit profile</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Name"><Input data-testid="edit-name" value={val("name")} onChange={(e) => setF({ ...f, name: e.target.value })} className="h-11 rounded-xl" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Current weight (kg)"><Input type="number" data-testid="edit-weight" value={val("current_weight")} onChange={(e) => setF({ ...f, current_weight: e.target.value })} className="h-11 rounded-xl" /></Field>
            <Field label="Target weight (kg)"><Input type="number" data-testid="edit-target" value={val("target_weight")} onChange={(e) => setF({ ...f, target_weight: e.target.value })} className="h-11 rounded-xl" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Age"><Input type="number" value={val("age")} onChange={(e) => setF({ ...f, age: e.target.value })} className="h-11 rounded-xl" /></Field>
            <Field label="Height (cm)"><Input type="number" value={val("height")} onChange={(e) => setF({ ...f, height: e.target.value })} className="h-11 rounded-xl" /></Field>
          </div>
        </div>
        <Button onClick={() => save({ ...f, age: f.age ? Number(f.age) : profile.age, height: f.height ? Number(f.height) : profile.height, current_weight: f.current_weight ? Number(f.current_weight) : profile.current_weight, target_weight: f.target_weight ? Number(f.target_weight) : profile.target_weight })} disabled={busy} data-testid="save-profile-btn" className="w-full rounded-full py-6 bg-primary hover:bg-primary/90">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save changes
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function GoalDialog({ open, setOpen, profile, setProfile }) {
  const [cal, setCal] = useState("");
  const { busy, save } = useSave(setProfile, setOpen);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-sm rounded-3xl" data-testid="goal-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">Daily calorie goal</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Your AI-calculated goal is recalculated from your profile. Save your profile to reset it automatically, or set a custom target here (informational).</p>
        <div className="text-center py-4">
          <span className="text-4xl font-display font-medium text-primary">{profile.calorie_goal}</span>
          <span className="text-muted-foreground"> kcal</span>
        </div>
        <Button onClick={() => setOpen(false)} className="w-full rounded-full py-6 bg-primary hover:bg-primary/90">Got it</Button>
      </DialogContent>
    </Dialog>
  );
}

function DietDialog({ open, setOpen, profile, setProfile }) {
  const { busy, save } = useSave(setProfile, setOpen);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-sm rounded-3xl" data-testid="diet-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">Dietary preference</DialogTitle></DialogHeader>
        <div className="space-y-2">
          {DIETS.map((d) => (
            <button key={d} data-testid={`diet-opt-${d.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => save({ dietary_preference: d })} disabled={busy} className={`w-full p-3 rounded-xl border text-left font-medium text-sm transition-colors ${profile.dietary_preference === d ? "bg-primary/5 border-primary" : "bg-card border-border hover:border-primary/40"}`}>
              {d}
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function NotifDialog({ open, setOpen, profile, setProfile }) {
  const [n, setN] = useState(profile.notifications || {});
  const { busy, save } = useSave(setProfile, setOpen);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const supported = pushSupported();

  useEffect(() => {
    if (open) isPushSubscribed().then(setPushOn);
  }, [open]);

  const togglePush = async (v) => {
    setPushBusy(true);
    try {
      if (v) {
        await subscribePush();
        setPushOn(true);
        toast.success("Background reminders enabled 🔔");
      } else {
        await unsubscribePush();
        setPushOn(false);
        toast.success("Background reminders turned off");
      }
    } catch (e) {
      toast.error(e.message || "Couldn't update push");
      setPushOn(await isPushSubscribed());
    } finally {
      setPushBusy(false);
    }
  };

  const sendTest = async () => {
    setPushBusy(true);
    try {
      const r = await api.pushTest();
      toast.success(r.sent > 0 ? "Test notification sent!" : "No active device found");
    } catch (e) {
      toast.error(e.message || "Could not send test");
    } finally {
      setPushBusy(false);
    }
  };

  const items = [
    ["meals", "Meal reminders"], ["progress", "Daily calorie progress"], ["hydration", "Hydration reminders"],
    ["weight", "Weight tracking"], ["motivation", "Daily motivation"], ["streak", "Streak reminders"],
  ];
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-sm rounded-3xl max-h-[85vh] overflow-y-auto" data-testid="notif-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">Notifications</DialogTitle></DialogHeader>

        <div className="rounded-2xl bg-secondary/40 border border-border p-4 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold flex items-center gap-1.5"><Bell className="w-4 h-4 text-primary" /> Background reminders</div>
              <div className="text-xs text-muted-foreground mt-0.5">Get an evening nudge even when Calorify is closed.</div>
            </div>
            {supported ? (
              <Switch checked={pushOn} disabled={pushBusy} onCheckedChange={togglePush} data-testid="push-toggle" />
            ) : (
              <span className="text-[10px] text-muted-foreground">Not supported</span>
            )}
          </div>
          {pushOn && (
            <button onClick={sendTest} disabled={pushBusy} data-testid="push-test-btn" className="text-xs font-semibold text-primary flex items-center gap-1 disabled:opacity-50">
              {pushBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Send a test notification
            </button>
          )}
        </div>

        <div className="text-xs font-semibold text-muted-foreground pt-1">Reminder types</div>
        <div className="space-y-1">
          {items.map(([k, l]) => (
            <div key={k} className="flex items-center justify-between py-2.5">
              <span className="text-sm font-medium">{l}</span>
              <Switch checked={!!n[k]} onCheckedChange={(v) => {
                setN({ ...n, [k]: v });
                if (v && "Notification" in window && Notification.permission === "default") {
                  Notification.requestPermission();
                }
              }} data-testid={`notif-${k}`} />
            </div>
          ))}
        </div>
        <Button onClick={() => save({ notifications: n })} disabled={busy} className="w-full rounded-full py-6 bg-primary hover:bg-primary/90">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-semibold text-foreground/70">{label}</Label>
      {children}
    </div>
  );
}
